import React, { useState, useCallback, ChangeEvent, useEffect } from "react";
import {
  Content,
  TitleContainer,
  FormContainer,
  ButtonsContainer,
  CheckboxContainer,
  ModalStyles,
} from "./styles";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { Autocomplete } from "@mui/material";
import { TextField } from "@mui/material";
import api from "./../../services/api";
import refreshToken from "./../../utils/refreshToken";
import { useAuth } from "./../../hooks/auth";
import { FiX } from "react-icons/fi";
import { useToast } from "../../hooks/toast";
import { useRefresh } from "../../hooks/refresh";
import * as Yup from "yup";
import getValidationErrors from "../../utils/getValidationError";
import { getApiErrorToastTitle } from "../../utils/apiErrorToast";
import axios, { CancelTokenSource } from "axios";
import OutsideClickHandler from "react-outside-click-handler";
import useModalFetch from "../../hooks/useModalFetch";
import ScheduleSkeleton from "./ScheduleSkeleton";

interface IUsers {
  id: number;
  first_name: string;
  email: string;
  phone: string;
}

interface IProperty {
  id: number;
  title: string;
}

interface IModalSchedule {
  open: boolean;
  param: any;
  functionToCloseModalSchedule(): void;
}

const getPropertiesOptions = (properties: IProperty[]) => {
  return properties.map((property) => ({
    value: property.id,
    label: `${property.id} - ${property.title}`,
  }));
};

const ORIGIN_SOURCES = [
  "OLX",
  "Facebook",
  "ZAP imóveis",
  "Viva Real",
  "Google",
  "Instagram",
  "Outro",
] as const;

type OriginSource = typeof ORIGIN_SOURCES[number];
const getOriginSourceOptions = () =>
  ORIGIN_SOURCES.map((s) => ({ value: s, label: s }));


const ModalSchedule: React.FC<IModalSchedule> = ({
  open,
  param,
  functionToCloseModalSchedule,
}: IModalSchedule) => {
  const { access_token, dateToExpires, user } = useAuth();
  const { addToast } = useToast();
  const { handleRefresh } = useRefresh();
  const {
    fetchedData: dataToShow,
    isFetching,
  } = useModalFetch(`/api/adm/schedule/panel`);

  const canManageSchedules = user?.role !== "consultant";

  useEffect(() => {
    if (!open || canManageSchedules) {
      return;
    }
    addToast({
      title: "Sem permissão para alterar a agenda",
      type: "error",
    });
    functionToCloseModalSchedule();
  }, [open, canManageSchedules, addToast, functionToCloseModalSchedule]);

  const [userSelected, setUserSelected] = useState({} as IUsers);
  const [email, setEmail] = useState("");
  const [users, setUsers] = useState<IUsers[]>([]);
  const [registerNewClient, setRegisterNewClient] = useState(false);
  const [clientIdSelected, setClientIdSelected] = useState<number>();
  const [propertyIdSelected, setPropertyIdSelected] = useState<number>();
  const [loadingRequestToSchedule, setLoadingRequestToSchedule] = useState(false);
  const [originSource, setOriginSource] = useState<OriginSource | "">("");
  const [videoCall, setVideoCall] = useState(false);

  let cancelToken: undefined | CancelTokenSource;

  useEffect(() => {
    if (!open) {
      setUserSelected({} as IUsers);
      setEmail("");
      setUsers([]);
      setRegisterNewClient(false);
      setClientIdSelected(undefined);
      setOriginSource("");
      setVideoCall(false);
    }
  }, [open]);

  const handleSearchChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const searchTerm = e.target.value;
      setEmail(searchTerm);

      if (cancelToken !== undefined) {
        cancelToken.cancel("Operation canceled due to new request.");
      }

      cancelToken = axios.CancelToken.source();

      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token}`;

        const response = await api.get("/api/adm/users", {
          params: {
            email: searchTerm,
          },
          cancelToken: cancelToken?.token,
        });

        if (response.data.data.users.length === 0) {
          addToast({
            title: "Nenhum usuário encontrado",
            type: "error",
          });
        }

        setUsers(response.data.data.users);
      } catch (err: any) {
      }
    },
    [cancelToken, access_token, dateToExpires, addToast]
  );

  const handleSelectUser = useCallback(
    (user_id: number | undefined) => {
      const user_selected = users.find((client) => client.id === user_id);
      setClientIdSelected(user_id);
      setUsers([]);

      if (!!user_selected) {
        setEmail(user_selected?.email);
        setUserSelected(user_selected);
      }
    },
    [users]
  );

  const handleSchedule = useCallback(async () => {
    if (!canManageSchedules) {
      addToast({
        title: "Sem permissão para alterar a agenda",
        type: "error",
      });
      return;
    }

    setLoadingRequestToSchedule(true);

    const propertyIdToUse = param?.propertyId || propertyIdSelected;

    let data;

    if (registerNewClient)
      data = {
        phone: userSelected.phone,
        email: userSelected.email,
        name: userSelected.first_name,
        propertyIdSelected: propertyIdToUse,
        originSource,
      };
    else {
      data = {
        clientIdSelected,
        propertyIdSelected: propertyIdToUse,
        originSource,
      };
    }

    try {
      let schema;
      const baseSchemaFields = {
        originSource: Yup.string().required("Selecione a origem"),
        propertyIdSelected: param?.propertyId
          ? Yup.number()
          : Yup.number().required("Selecione um imóvel"),
      };

      if (registerNewClient) {
        schema = Yup.object().shape({
          email: Yup.string().email("Digite um e-mail"),
          phone: Yup.string().required("Digite um telefone"),
          name: Yup.string().required("Digite um nome"),
          ...baseSchemaFields,
        });
      } else {
        schema = Yup.object().shape({
          clientIdSelected: Yup.number().required("Selecione um e-mail"),
          ...baseSchemaFields,
        });
      }

      await schema.validate(data, {
        abortEarly: false,
      });

      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token}`;

      let body = {};
      const scheduleParam = typeof param === 'string' ? param : param?.schedule;
      const formattedSchedule = scheduleParam ? scheduleParam.replace(/:00(?=\|)/, '') : scheduleParam;

      if (registerNewClient) {
        body = {
          schedule: formattedSchedule,
          property_id: propertyIdToUse,
          user_data: { ...userSelected },
          origin_source: originSource,
          video_call: videoCall,
        };
      } else {
        body = {
          schedule: formattedSchedule,
          property_id: propertyIdToUse,
          client_id: clientIdSelected,
          origin_source: originSource,
          video_call: videoCall,
        };
      }

      const response = await api.post("/api/adm/schedules/mark", { ...body });

      if (!!response.data.success) {
        addToast({
          title: "Agendamento concluído",
          type: "success",
          description: "",
        });

        handleRefresh();

        setTimeout(() => {
          functionToCloseModalSchedule();
        }, 500);
      }

      setLoadingRequestToSchedule(false);
    } catch (err: any) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        addToast({
          title: errors[Object.keys(errors)[0]],
          type: "info",
          description: "",
        });
      } else {
        addToast({
          title: getApiErrorToastTitle(err, "Erro ao criar agendamento"),
          type: "error",
        });
      }

      setLoadingRequestToSchedule(false);
    }
  }, [
    param,
    propertyIdSelected,
    clientIdSelected,
    originSource,
    videoCall,
    dateToExpires,
    access_token,
    addToast,
    functionToCloseModalSchedule,
    handleRefresh,
    registerNewClient,
    userSelected,
    canManageSchedules,
  ]);

  const handleToggleRegisterNewClient = () => {
    setRegisterNewClient((prevData) => !prevData);
    setUserSelected((prevValue) => ({ ...prevValue, email: "" }));
    setEmail("");
  }

  let content = <ScheduleSkeleton />;
  if (!isFetching && Object.keys(dataToShow).length !== 0)
    content = (
      <Content>
        <TitleContainer>
          <h3>
            {param?.propertyId
              ? "Novo Agendamento (Mesmo Imóvel)"
              : "Agendamento"
            }
          </h3>
        </TitleContainer>
        <hr />
        <FormContainer>
          <CheckboxContainer>
            <input
              checked={registerNewClient}
              type="checkbox"
              id="registered"
              onChange={handleToggleRegisterNewClient}
            />
            <label htmlFor="registered">Novo Cliente</label>
          </CheckboxContainer>
          <CheckboxContainer>
            <input
              checked={videoCall}
              type="checkbox"
              id="video-call"
              onChange={(e) => setVideoCall(e.target.checked)}
            />
            <label htmlFor="video-call">Visita por videochamada</label>
          </CheckboxContainer>
          <p>E-mail:</p>
          <input
            value={registerNewClient ? userSelected.email : email}
            onChange={
              registerNewClient
                ? (e) =>
                  setUserSelected((prevValue) => ({
                    ...prevValue,
                    email: e.target.value,
                  }))
                : email.length <= 2
                  ? (e) => setEmail(e.target.value)
                  : handleSearchChange
            }
          />

          {users.length >= 1 && (
            <OutsideClickHandler
              onOutsideClick={() => {
                setEmail("");
                setUsers([]);
              }}
            >
              <div id="users">
                {users.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => handleSelectUser(user.id)}
                  >
                    {user.email}
                  </button>
                ))}
              </div>
            </OutsideClickHandler>
          )}

          {(!!userSelected.first_name || registerNewClient) && (
            <>
              <p>Nome: </p>
              <input
                type="text"
                readOnly={!registerNewClient}
                value={userSelected.first_name}
                onChange={(e) =>
                  setUserSelected((prevValue) => ({
                    ...prevValue,
                    first_name: e.target.value,
                  }))
                }
              />

              <p>Telefone: </p>
              <input
                type="text"
                readOnly={!registerNewClient}
                value={userSelected.phone}
                onChange={(e) =>
                  setUserSelected((prevValue) => ({
                    ...prevValue,
                    phone: e.target.value,
                  }))
                }
              />
            </>
          )}

          {param?.propertyId ? (
            <>
              <p>Imóvel:</p>
              <div className="property-pre-selected">
                <strong>Imóvel {param.propertyId}</strong>
                <p>Pré-selecionado do agendamento anterior</p>
              </div>
            </>
          ) : (
            <>
              <p>Imóvel:</p>
              <Autocomplete
                id="combo-box-demo"
                options={getPropertiesOptions(dataToShow.properties)}
                getOptionLabel={(option) => option.label}
                style={{ width: "100%" }}
                noOptionsText="Sem opções"
                onChange={(e, value) => setPropertyIdSelected(value?.value)}
                renderInput={(params) => (
                  <TextField {...params} label="" margin="normal" />
                )}
              />
            </>
          )}
          <p>Origem:</p>
          <Autocomplete
            id="origin-source"
            options={getOriginSourceOptions()}
            getOptionLabel={(option) => option.label}
            style={{ width: "100%" }}
            noOptionsText="Sem opções"
            value={
              originSource
                ? ({ value: originSource as OriginSource, label: originSource } as any)
                : null
            }
            onChange={(e, value) => {
              const next = value?.value ?? "";
              setOriginSource(next as OriginSource | "");
            }}
            renderInput={(params) => <TextField {...params} label="" margin="normal" />}
          />
        </FormContainer>

        <hr />

        <ButtonsContainer>
          {!!loadingRequestToSchedule ? (
            <p>Carregando...</p>
          ) : (
            <button onClick={handleSchedule}>Agendar</button>
          )}
        </ButtonsContainer>
      </Content>
    );

  return (
    <ModalStyles>
      <Modal
        open={open}
        onClose={functionToCloseModalSchedule}
        classNames={{
          modal: "modalSchedule",
        }}
        center
        closeIcon={<FiX size={18} color="#000" />}
      >
        {content}
      </Modal>
    </ModalStyles>
  );
};

export default ModalSchedule;